import { apiRaw } from '../api';

export default class Helper {
    // Agrega esta función helper antes del return
    static capitalizeFirstLetter = (string) => {
        return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
    }

    static getTurnoColors = (turno) => {
        const estados = {
            // Texto blanco sobre este gris daba ~2.6:1 de contraste (WCAG pide 4.5:1 para texto normal).
            "Pendiente":   ["#a0a0a0", "#333"],
            // #386def sobre este azul claro daba ~3.4:1 (N4, ronda 7 de QA) — azul más oscuro.
            "Asignado":    ["#bee4fa", "#1E3FA0"],
            "Finalizado":  ["#e6f9ed", "#1a7f37"],   
            "Inactivo":    ["#fee9e9", "#ea4b29"],     
        };
        if (turno.estado && estados[turno.estado]) {
            return estados[turno.estado];
        }
        return ["#fee9e9", "#ea4b29"];
    }

    // Función para formatear el número de teléfono
    static formatPhoneNumber = (value) => {
        // Remover todo excepto números
        const numbers = value.replace(/\D/g, '');
        
        // Si no hay números, retornar solo el prefijo
        if (!numbers) return '+52-';
        
        // Asegurarnos de que el número comience con 52
        let phoneNumber = numbers;
        if (!phoneNumber.startsWith('52')) {
            phoneNumber = '52' + phoneNumber;
        }
        
        // Formatear el número
        let formatted = '+52-';
        const digits = phoneNumber.slice(2); // Remover el 52 inicial
        
        for (let i = 0; i < digits.length && i < 10; i++) {
            if (i === 2 || i === 4 || i === 6 || i === 8) {
                formatted += '-';
            }
            formatted += digits[i];
        }
        return formatted;
    }

    // Función para limpiar el formato del teléfono
    static cleanPhoneNumber = (formattedNumber) => {
        return formattedNumber.replace(/\D/g, '');
    }

    // Función para normalizar la URL de la imagen
    static normalizarUrlImagen = (url, baseURL) => {
        if (!url) return '';
        // Si la URL es relativa (empieza con ./), convertirla a URL completa
        if (url.startsWith('./')) {
            // Remover el ./ y construir la URL completa
            const rutaRelativa = url.replace('./', '');
            return `${baseURL}${rutaRelativa}`;
        }
        // Si ya es una URL completa, devolverla tal cual
        return url;
    }

    // Definiciones de filtros de estado reutilizables
    // N4 (ronda 7 de QA): "Pendiente" (texto blanco sobre #a0a0a0, ~2.6:1) y "Asignado"/
    // "aprobado_oculto" (#386def sobre #bee4fa, ~3.4:1) no llegaban al 4.5:1 que pide WCAG —
    // mismos colores de fondo/borde, solo el color del texto cambia.
    static estadoFiltros = [
        { key: 'pendiente', label: 'Pendiente', bg: '#a0a0a0', color: '#333', border: '#888' },
        { key: 'asignado', label: 'Asignado', bg: '#bee4fa', color: '#1E3FA0', border: '#386def' },
        { key: 'finalizado', label: 'Finalizado', bg: '#e6f9ed', color: '#1a7f37', border: '#1a7f37' },
        { key: 'inactivo', label: 'Inactivo', bg: '#fee9e9', color: '#ea4b29', border: '#ea4b29' },
    ];

    // Filtros de estado de aprobación para talleres
    static estadoFiltrosTalleres = [
        { key: 'pendiente', label: 'Pendiente', bg: '#f0f0f0', color: '#666', border: '#999' },
        { key: 'aprobado', label: 'Aprobado', bg: '#e6f9ed', color: '#1a7f37', border: '#1a7f37' },
        { key: 'aprobado_oculto', label: 'Aprobado (Oculto)', bg: '#bee4fa', color: '#1E3FA0', border: '#386def' },
    ];

    // Función para filtrar turnos por estados seleccionados, usuario, texto y servicios
    static filterTurnosByEstados = ({
        turnos,
        selectedEstados,
        usuarioLegued,
        filterText,
        serviciosFiltrados
    }) => {
        if (!selectedEstados || selectedEstados.length === 0) {
            return turnos?.filter(
                (turno) =>
                    (turno?.nombre?.toLowerCase()?.includes(filterText) ||
                        turno?.dni?.includes(filterText) ||
                        turno?.telefono?.includes(filterText)) &&
                    (serviciosFiltrados.map(servicio => servicio.value).includes(turno?.idServicio) || serviciosFiltrados.length === 0)
            );
        }
        // Roles que pueden ver todos los turnos de sus Cesecos en cualquier estado
        const rolesVerTodos = ['coord_ceseco', 'coord_programa', 'secretaria', 'admin', 'direccion'];
        let estadosMap;
        if (rolesVerTodos.includes(usuarioLegued?.rol)) {
            estadosMap = {
                pendiente: (turno) => turno.estado === 'Pendiente',
                asignado: (turno) => turno.estado === 'Asignado',
                finalizado: (turno) => turno.estado === 'Finalizado',
                inactivo: (turno) => turno.estado === 'Inactivo',
            };
        } else {
            estadosMap = {
                pendiente: (turno) => turno.idTerapeuta === null && turno.estado === 'Pendiente',
                asignado: (turno) => turno.idTerapeuta === usuarioLegued.idUsuario && turno.estado === 'Asignado',
                finalizado: (turno) => turno.idTerapeuta === usuarioLegued.idUsuario && turno.estado === 'Finalizado',
                inactivo: (turno) => turno.idTerapeuta === usuarioLegued.idUsuario && turno.estado === 'Inactivo',
            };
        }
        return turnos?.filter(
            (turno) =>
                (turno?.nombre?.toLowerCase()?.includes(filterText) ||
                    turno?.dni?.includes(filterText) ||
                    turno?.telefono?.includes(filterText)) &&
                selectedEstados.some(estado => estadosMap[estado](turno)) &&
                (serviciosFiltrados.map(servicio => servicio.value).includes(turno?.idServicio) || serviciosFiltrados.length === 0)
        );
    }

    // Configuración reutilizable para AsyncSelect de búsqueda de turnos
    static searchTurnosAsyncSelectConfig({ setTurnoSeleccionado, setModalIsOpen, baseURL, Helper, usuarioLegued }) {
        return {
            cacheOptions: false,
            loadOptions: async (inputValue) => {
                if (!inputValue || inputValue.length < 2) return [];
                const params = new URLSearchParams({ q: inputValue });

                // Terapeuta: solo ve sus citas + pendientes sin asignar de su Ceseco
                if (usuarioLegued?.rol === 'terapeuta' && usuarioLegued?.idUsuario) {
                    params.append('idTerapeuta', usuarioLegued.idUsuario);
                }

                // Restricción de Ceseco para roles no globales
                const rolesGlobales = ['admin', 'direccion'];
                if (!rolesGlobales.includes(usuarioLegued?.rol)) {
                    const ids = usuarioLegued?.idCategorias?.length
                        ? usuarioLegued.idCategorias
                        : usuarioLegued?.idCategoria
                            ? [usuarioLegued.idCategoria]
                            : [];
                    if (ids.length > 0) {
                        params.append('idCategorias', ids.join(','));
                    }
                }

                const res = await apiRaw(`/turnos/search?${params.toString()}`);
                const data = await res.json();
                if (!data.results) return [];
                return data.results.map(turno => ({
                    value: turno.idTurno,
                    label: turno.nombre,
                    turno
                }));
            },
            placeholder: "Buscar por nombre, teléfono, Ceseco, terapeuta o servicio",
            noOptionsMessage: () => 'Sin coincidencias',
            onChange: option => {
                if (option && option.turno) {
                    setTurnoSeleccionado(option.turno);
                    setModalIsOpen(true);
                }
            },
            isClearable: true,
            menuPortalTarget: typeof window !== 'undefined' ? document.body : undefined,
            styles: {
                control: (provided) => ({
                    ...provided,
                    minHeight: '38px',
                    borderRadius: 8,
                    fontSize: '1rem',
                    boxShadow: 'none',
                    borderColor: '#ccc',
                    minWidth: 420,
                    maxWidth: 480,
                }),
                menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    borderRadius: 8,
                    minWidth: 420,
                    maxWidth: 480,
                }),
                menuPortal: base => ({ ...base, zIndex: 9999, minWidth: 420, maxWidth: 480 }),
            },
            formatOptionLabel: ({ turno }) => {
                const colors = Helper.getTurnoColors({ estado: turno.estado });
                let fecha = turno.fecha;
                let hora = turno.horaInicio && turno.horaFin ? `${turno.horaInicio} - ${turno.horaFin}` : '';
                if (turno.dias && Array.isArray(turno.dias) && turno.dias.length > 0) {
                    fecha = turno.dias[0].dia;
                    hora = `${turno.dias[0].horaInicio} - ${turno.dias[0].horaFin}`;
                }
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                borderRadius: 3,
                                background: colors[0],
                                border: `1.5px solid ${colors[1]}`,
                                marginRight: 6
                            }} />
                            <span style={{ fontWeight: 600 }}>{turno.nombre}</span>
                            <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{turno.telefono}</span>
                            <span style={{ fontSize: 12, color: '#888', marginLeft: 8 }}>{turno.dni}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <span style={{ color: '#386def' }}>{turno.ceseco}</span>
                            <span style={{ color: '#1a7f37' }}>{turno.servicio}</span>
                            <span style={{ color: '#ea4b29' }}>{turno.terapeuta}</span>
                            <span style={{ color: '#555', marginLeft: 8 }}>{fecha} {hora}</span>
                            <span style={{
                                background: colors[0],
                                color: colors[1],
                                borderRadius: 4,
                                padding: '0 6px',
                                fontWeight: 600,
                                fontSize: 12,
                                marginLeft: 8
                            }}>{turno.estado}</span>
                        </div>
                    </div>
                );
            }
        };
    }
}
